import { useEffect, useCallback, useRef, useState } from "react";
import { MediaStage } from "@/core/types/mediaTypes";
import { stageKeyboardConfig, LEADER_KEY } from "./actions/StageActions";

interface KeyboardHandlers {
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  onStageChange?: (stage: MediaStage) => void;
  onDownload?: () => void;
  onClose?: () => void;
  onPlayPause?: () => void;
  onSkipForward?: () => void;
  onSkipBackward?: () => void;
  onRemove?: () => void;
  onDownloadLeader?: () => void;
  onAddToCollection?: () => void;
}

interface UseReviewKeyboardProps {
  isOpen: boolean;
  handlers: KeyboardHandlers;
  canNavigateNext?: boolean;
  canNavigatePrev?: boolean;
  editorMode?: boolean;
  onLeaderModeChange?: (active: boolean) => void;
}

const LEADER_TIMEOUT_MS = 1500;

export const useReviewKeyboard = ({
  isOpen,
  handlers,
  canNavigateNext = true,
  canNavigatePrev = true,
  editorMode = false,
  onLeaderModeChange,
}: UseReviewKeyboardProps) => {
  const [leaderModeActive, setLeaderModeActive] = useState(false);
  const leaderTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const exitLeaderMode = useCallback(() => {
    if (leaderTimeoutRef.current) {
      clearTimeout(leaderTimeoutRef.current);
      leaderTimeoutRef.current = null;
    }
    setLeaderModeActive(false);
    onLeaderModeChange?.(false);
  }, [onLeaderModeChange]);

  const enterLeaderMode = useCallback(() => {
    setLeaderModeActive(true);
    onLeaderModeChange?.(true);

    if (leaderTimeoutRef.current) {
      clearTimeout(leaderTimeoutRef.current);
    }

    leaderTimeoutRef.current = setTimeout(() => {
      exitLeaderMode();
    }, LEADER_TIMEOUT_MS);
  }, [onLeaderModeChange, exitLeaderMode]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Don't handle if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      let handled = false;
      const key = e.key.toLowerCase();

      // Leader key pressed - enter leader mode
      if (e.key === LEADER_KEY) {
        handled = true;
        if (leaderModeActive) {
          exitLeaderMode();
        } else {
          enterLeaderMode();
        }
      }
      // Leader mode is active - handle stage shortcuts and leader-based actions
      else if (leaderModeActive) {
        exitLeaderMode();

        if (!editorMode && handlers.onStageChange) {
          const stage = stageKeyboardConfig[key];
          if (stage) {
            handled = true;
            handlers.onStageChange(stage);
          }
        }

        // R for remove (in collection context)
        if (key === "r" && handlers.onRemove) {
          handled = true;
          handlers.onRemove();
        }

        // X for download (in collection context with leader key)
        if (key === "x" && handlers.onDownloadLeader) {
          handled = true;
          handlers.onDownloadLeader();
        }

        // C for add to collection
        if (key === "c" && handlers.onAddToCollection) {
          handled = true;
          handlers.onAddToCollection();
        }
      }
      // Regular shortcuts (no leader required)
      else {
        switch (e.key) {
          case "ArrowLeft":
            if (canNavigatePrev && handlers.onNavigatePrev) {
              handled = true;
              handlers.onNavigatePrev();
            }
            break;

          case "ArrowRight":
            if (canNavigateNext && handlers.onNavigateNext) {
              handled = true;
              handlers.onNavigateNext();
            }
            break;

          case " ":
            if (handlers.onPlayPause) {
              handled = true;
              handlers.onPlayPause();
            }
            break;

          case "j":
          case "J":
            if (handlers.onSkipBackward) {
              handled = true;
              handlers.onSkipBackward();
            }
            break;

          case "l":
          case "L":
            if (handlers.onSkipForward) {
              handled = true;
              handlers.onSkipForward();
            }
            break;

          case "Escape":
            if (handlers.onClose) {
              handled = true;
              handlers.onClose();
            }
            break;
        }
      }

      if (handled) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [
      handlers,
      canNavigateNext,
      canNavigatePrev,
      editorMode,
      leaderModeActive,
      enterLeaderMode,
      exitLeaderMode,
    ]
  );

  useEffect(() => {
    if (!isOpen) {
      exitLeaderMode();
      return;
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown, exitLeaderMode]);

  return { leaderModeActive };
};
